import React, { useState, useEffect, useRef } from 'react';
import { useUserAuth } from '../../context/userAuthContext';
import { Play, ChevronLeft, ChevronRight } from 'lucide-react';

// Traducciones para el HeroSection
const translations = {
  en: {
    summit: 'FRONIOS SUMMIT 2025',
    title: 'Advanced Technology Solutions',
    description: 'Explore how FroniosAI technologies are enhancing productivity across industries.',
    button: 'View Presentation',
    tabs: [
      {
        category: 'TECHNOLOGY',
        title: 'NVIDIA Chip Technology: Market Applications',
        author: 'P. Aguirre, Technology Director',
        backgroundImage: 'https://s3.amazonaws.com/cms.ipressroom.com/219/files/20240/generative-ai-tensor-core-gpus-llms-ai-software-and-tools-for-rtx-pcs-image.jpg'
      },
      {
        category: 'STRATEGY',
        title: 'Strategic Technology Selection for Enterprise Solutions',
        backgroundImage: 'https://firebasestorage.googleapis.com/v0/b/fronios-48861.firebasestorage.app/o/covers%2Fpremium_photo-1683121710572-7723bd2e235d.avif?alt=media&token=5ced2611-718d-4868-b55b-e791cd8d8427'
      },
      {
        category: 'AI SOLUTIONS',
        title: 'OpenAI Integration: Business Transformation',
        backgroundImage: 'https://firebasestorage.googleapis.com/v0/b/fronios-48861.firebasestorage.app/o/covers%2FThe-Security-Hole-at-the-Heart-of-ChatGPT-and-Bing-Security-GettyImages-1264516882.webp?alt=media&token=26013561-fe4d-4f2d-a36c-e7eb34c1b6d9'
      }
    ]
  },
  es: {
    summit: 'FRONIOS SUMMIT 2025',
    title: 'Soluciones Tecnológicas Avanzadas',
    description: 'Explore cómo las tecnologías de FroniosAI mejoran la productividad en diversos sectores.',
    button: 'Ver Presentación',
    tabs: [
      {
        category: 'TECNOLOGÍA',
        title: 'Tecnología de Chips NVIDIA: Aplicaciones de Mercado',
        author: 'P. Aguirre, Director de Tecnología',
        backgroundImage: 'https://s3.amazonaws.com/cms.ipressroom.com/219/files/20240/generative-ai-tensor-core-gpus-llms-ai-software-and-tools-for-rtx-pcs-image.jpg'
      },
      {
        category: 'ESTRATEGIA',
        title: 'Selección Estratégica de Tecnología para Soluciones Empresariales',
        backgroundImage: 'https://firebasestorage.googleapis.com/v0/b/fronios-48861.firebasestorage.app/o/covers%2Fpremium_photo-1683121710572-7723bd2e235d.avif?alt=media&token=5ced2611-718d-4868-b55b-e791cd8d8427'
      },
      {
        category: 'SOLUCIONES IA',
        title: 'Integración OpenAI: Transformación Empresarial',
        backgroundImage: 'https://firebasestorage.googleapis.com/v0/b/fronios-48861.firebasestorage.app/o/covers%2FThe-Security-Hole-at-the-Heart-of-ChatGPT-and-Bing-Security-GettyImages-1264516882.webp?alt=media&token=26013561-fe4d-4f2d-a36c-e7eb34c1b6d9'
      }
    ]
  }
};

const HeroSection = () => {
  // Obtener el idioma actual del contexto
  const { currentLanguage } = useUserAuth();
  const t = translations[currentLanguage];

  // Estado para manejar el slide activo
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [progressWidth, setProgressWidth] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const progressIntervalRef = useRef(null);
  const progressDuration = 6000; // 6 segundos por slide

  // Función para cambiar al siguiente slide
  const goToNextSlide = () => {
    setActiveSlideIndex((prevIndex) =>
      (prevIndex + 1) % t.tabs.length
    );
    resetProgress();
  };

  // Función para cambiar al slide anterior
  const goToPrevSlide = () => {
    setActiveSlideIndex((prevIndex) =>
      prevIndex === 0 ? t.tabs.length - 1 : prevIndex - 1
    );
    resetProgress();
  };

  // Función para cambiar a un slide específico
  const goToSlide = (index) => {
    setActiveSlideIndex(index);
    resetProgress();
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      resetProgress();
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }
  };

  // Resetear y comenzar el progreso
  const resetProgress = () => {
    // Limpiar cualquier intervalo existente
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    // Restablecer el ancho del progreso
    setProgressWidth(0);

    // Solo comenzar si está en modo play
    if (!isPlaying) return;

    // Comenzar nuevo intervalo de progreso
    progressIntervalRef.current = setInterval(() => {
      setProgressWidth((prevWidth) => {
        // Si ya llegamos al 100%, cambiar al siguiente slide
        if (prevWidth >= 100) {
          goToNextSlide();
          return 0;
        }
        // Incrementar el progreso
        return prevWidth + (100 / (progressDuration / 50)); // 50ms es el intervalo
      });
    }, 50); // Actualizar cada 50ms para un movimiento suave
  };

  // Efecto para iniciar el progreso cuando el componente se monta
  useEffect(() => {
    if (isPlaying) {
      resetProgress();
    }

    // Limpiar el intervalo cuando el componente se desmonte
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [activeSlideIndex, isPlaying]);

  // Obtener el slide actual
  const currentSlide = t.tabs[activeSlideIndex];

  // Mapear colores del logo a cada slide
  const slideColors = [
    { primary: 'orange-500', secondary: 'orange-400', accent: 'orange-300' },
    { primary: 'blue-500', secondary: 'blue-400', accent: 'blue-300' },
    { primary: 'teal-500', secondary: 'teal-400', accent: 'teal-300' }
  ];

  const currentColor = slideColors[activeSlideIndex];

  return (
    <div className="relative overflow-hidden">
      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 z-10"></div>

      {/* Side gradients for extra depth */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30 z-10"></div>

      {/* Hero background image - Responsive height */}
      <div
        className="w-full h-[60vh] sm:h-[70vh] lg:h-[80vh] bg-cover bg-center transition-all duration-700 ease-in-out transform scale-105"
        style={{
          backgroundImage: `url('${currentSlide.backgroundImage}')`,
        }}
      ></div>

      {/* Navigation arrows - Desktop only */}
      <div className="hidden lg:block">
        <button
          onClick={goToPrevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={goToNextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* Hero content - Responsive positioning */}
      <div className="absolute inset-0 z-20 flex flex-col justify-center lg:justify-end container mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pb-28">
        <div className="max-w-4xl lg:max-w-5xl text-center lg:text-left">
          {/* Summit badge */}
          <div className="inline-flex items-center mb-4 lg:mb-6">
            <div className={`px-4 py-2 bg-gradient-to-r from-${currentColor.primary} to-${currentColor.secondary} rounded-full text-white text-sm font-semibold shadow-lg backdrop-blur-sm`}>
              {t.summit}
            </div>
          </div>

          {/* Main title - Responsive sizing */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-4 lg:mb-6 leading-tight">
            {currentSlide.title}
          </h1>

          {/* Author info */}
          {currentSlide.author && (
            <div className={`inline-flex items-center mb-4 px-3 py-1 bg-${currentColor.primary}/20 rounded-full backdrop-blur-sm`}>
              <p className={`text-sm text-${currentColor.accent} font-medium`}>
                {currentSlide.author}
              </p>
            </div>
          )}

          {/* Description */}
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 lg:mb-8 max-w-3xl mx-auto lg:mx-0 leading-relaxed">
            {t.description}
          </p>

          {/* CTA Button with gradient */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center lg:justify-start">
            <button className={`group relative px-8 py-4 bg-gradient-to-r from-${currentColor.primary} to-${currentColor.secondary} text-white font-semibold rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1`}>
              <div className="flex items-center space-x-2">
                <Play className="h-5 w-5" />
                <span>{t.button}</span>
              </div>
              <div className="absolute inset-0 bg-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>

            {/* Play/Pause control - Mobile friendly */}
            <button
              onClick={togglePlayPause}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl backdrop-blur-sm transition-all duration-300 font-medium"
            >
              {isPlaying ? 'Pause' : 'Play'} Slideshow
            </button>
          </div>
        </div>
      </div>

      {/* Bottom navigation tabs - Improved mobile experience */}
      <div className="absolute bottom-0 left-0 right-0 z-30">
        {/* Mobile swipe indicators */}
        <div className="lg:hidden flex justify-center space-x-2 pb-4">
          {t.tabs.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-8 h-1 rounded-full transition-all duration-300 ${index === activeSlideIndex
                  ? `bg-${currentColor.primary}`
                  : 'bg-white/30'
                }`}
            />
          ))}
        </div>

        {/* Desktop tabs */}
        <div className="hidden lg:block bg-gradient-to-r from-black/90 via-black/80 to-black/90 backdrop-blur-sm">
          <div className="container mx-auto px-8">
            <div className="flex py-6 space-x-6">
              {t.tabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`
                    relative flex-1 transition-all duration-500 group
                    ${index === activeSlideIndex
                      ? 'opacity-100 transform scale-105'
                      : 'opacity-70 hover:opacity-100'
                    }
                  `}
                >
                  {/* Progress bar */}
                  {index === activeSlideIndex && isPlaying && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r from-${currentColor.primary} to-${currentColor.secondary} transition-all duration-75 rounded-full`}
                        style={{ width: `${progressWidth}%` }}
                      ></div>
                    </div>
                  )}

                  <div className="flex flex-col items-start pt-3 text-left">
                    <div className={`text-xs font-semibold mb-1 transition-colors duration-300 ${index === activeSlideIndex
                        ? `text-${currentColor.accent}`
                        : 'text-gray-400 group-hover:text-gray-300'
                      }`}>
                      {tab.category}
                    </div>
                    <div className={`text-sm font-medium transition-colors duration-300 ${index === activeSlideIndex
                        ? 'text-white'
                        : 'text-gray-300 group-hover:text-white'
                      }`}>
                      {tab.title}
                    </div>
                  </div>

                  {/* Hover effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r from-${currentColor.primary}/10 to-${currentColor.secondary}/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile navigation arrows */}
      <div className="lg:hidden absolute bottom-20 left-4 right-4 flex justify-between z-30">
        <button
          onClick={goToPrevSlide}
          className="p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-300 backdrop-blur-sm"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={goToNextSlide}
          className="p-3 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-300 backdrop-blur-sm"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default HeroSection;